import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Typography,
    Button,
    Space,
    message,
    Spin,
    Breadcrumb,
    Modal,
    Steps,
    Alert,
    Checkbox,
    Row,
    Col,
    Dropdown
} from 'antd';
import {
    HomeOutlined,
    EditOutlined,
    ArrowLeftOutlined,
    EyeOutlined,
    PlusOutlined,
    CheckCircleOutlined,
    PictureOutlined,
    RocketOutlined,
    MoreOutlined
} from '@ant-design/icons';
import auctionService from '../../services/auctionService';
import DashboardLayout from '../Dashboard/DashboardLayout';
import ImageUpload from './ImageUpload';

const { Title, Text } = Typography;

const LotImages = () => {
    const { auctionSlug, lotSlug } = useParams();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [lot, setLot] = useState(null);
    const [showSuccessModal, setShowSuccessModal] = useState(false);
    const [showSuccessAlert, setShowSuccessAlert] = useState(false);
    const [dontShowAgain, setDontShowAgain] = useState(false);
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
    const [isSmallMobile, setIsSmallMobile] = useState(window.innerWidth < 576);

    useEffect(() => {
        loadLot();
        // Check if user has previously chosen not to show the modal
        const dontShow = localStorage.getItem('dontShowLotImagesSuccessModal');
        if (dontShow === 'true') {
            setShowSuccessModal(false);
        }
    }, [auctionSlug, lotSlug]);

    // Handle window resize for responsive design
    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth < 768);
            setIsSmallMobile(window.innerWidth < 576);
        };

        window.addEventListener('resize', handleResize);
        handleResize(); // Initial check

        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const loadLot = async () => {
        try {
            setLoading(true);
            const response = await auctionService.getLot(auctionSlug, lotSlug);
            setLot(response.data);
        } catch (error) {
            console.error('Error loading lot:', error);
            message.error('Failed to load lot details');
            navigate(`/auctions/${auctionSlug}`);
        } finally {
            setLoading(false);
        }
    };

    const isAuctionCompleted = lot?.auction?.status === 'completed';

    const handleDontShowAgain = (e) => {
        setDontShowAgain(e.target.checked);
        if (e.target.checked) {
            localStorage.setItem('dontShowLotImagesSuccessModal', 'true');
        } else {
            localStorage.removeItem('dontShowLotImagesSuccessModal');
        }
    };

    const handleImageUploadSuccess = () => {
        if (!localStorage.getItem('dontShowLotImagesSuccessModal')) {
            setShowSuccessModal(true);
        }
        setShowSuccessAlert(true);
    };

    const handleCreateMoreLots = () => {
        setShowSuccessModal(false);
        navigate(`/lots/create?auction_slug=${auctionSlug}`);
    };

    // Menu items for mobile dropdown
    const actionMenuItems = [
        {
            key: 'my-lots',
            label: 'My Lots',
            icon: <ArrowLeftOutlined />,
            onClick: () => navigate(`/my-lots`)
        },
        {
            key: 'view-lot',
            label: 'View Lot',
            icon: <EyeOutlined />,
            onClick: () => navigate(`/auctions/${auctionSlug}/lots/${lotSlug}`)
        },
        {
            key: 'edit-lot',
            label: 'Edit Lot',
            icon: <EditOutlined />,
            onClick: () => navigate(`/auctions/${auctionSlug}/lots/${lotSlug}/edit`)
        },
        {
            key: 'create-more',
            label: 'Create More Lots',
            icon: <PlusOutlined />,
            onClick: () => navigate(`/lots/create?auction_slug=${auctionSlug}`)
        }
    ];

    return (
        <DashboardLayout>
            <Spin spinning={loading}>
                <div style={{ padding: '0 16px' }}>
                    <Breadcrumb
                        items={[
                            {
                                title: <a onClick={() => navigate('/')}><HomeOutlined /></a>
                            },
                            {
                                title: <a onClick={() => navigate('/my-auctions')}>My Auctions</a>
                            },
                            {
                                title: <a onClick={() => navigate(`/auctions/${auctionSlug}`)}>{auctionSlug}</a>
                            },
                            {
                                title: <a onClick={() => navigate(`/auctions/${auctionSlug}/lots/${lotSlug}`)}>{lot?.title}</a>
                            },
                            {
                                title: 'Images'
                            }
                        ]}
                        style={{ 
                            marginBottom: '24px',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis'
                        }}
                    />

                    <div style={{ marginTop: 16 }}>
                        <Space direction="vertical" size="large" style={{ width: '100%' }}>
                            <Row gutter={[16, 16]} align="middle">
                                <Col xs={24} sm={24} md={12} lg={12}>
                                    <Title 
                                        level={2} 
                                        style={{ 
                                            margin: 0,
                                            fontSize: 'clamp(20px, 4vw, 24px)'
                                        }}
                                    >
                                        Lot Images
                                    </Title>
                                </Col>
                                <Col xs={24} sm={24} md={12} lg={12}>
                                    <div style={{ 
                                        display: 'flex', 
                                        justifyContent: 'flex-end',
                                        flexWrap: 'wrap',
                                        gap: '8px'
                                    }}>
                                        {/* Desktop buttons */}
                                        {!isMobile && (
                                            <Space size="small" wrap>
                                                {/* <Button 
                                                    icon={<ArrowLeftOutlined />}
                                                    onClick={() => navigate(`/my-lots`)}
                                                >
                                                    My Lots
                                                </Button> */}
                                                <Button 
                                                    icon={<EyeOutlined />}
                                                    onClick={() => navigate(`/auctions/${auctionSlug}/lots/${lotSlug}`)}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    View Lot
                                                </Button>
                                                <Button 
                                                    type="primary"
                                                    icon={<EditOutlined />}
                                                    onClick={() => navigate(`/auctions/${auctionSlug}/lots/${lotSlug}/edit`)}
                                                >
                                                    Edit Lot
                                                </Button>
                                                <Button
                                                    type="primary"
                                                    icon={<PlusOutlined />}
                                                    onClick={() => navigate(`/lots/create?auction_slug=${auctionSlug}`)}
                                                >
                                                    Create More Lots
                                                </Button>
                                            </Space>
                                        )}
                                        
                                        {/* Mobile dropdown */}
                                        {isMobile && (
                                            <Dropdown
                                                menu={{ items: actionMenuItems }}
                                                trigger={['click']}
                                                placement="bottomRight"
                                            >
                                                <Button icon={<MoreOutlined />}>
                                                    Actions
                                                </Button>
                                            </Dropdown>
                                        )}
                                    </div>
                                </Col>
                            </Row>

                            {showSuccessAlert && (
                                <Alert
                                    message="Images uploaded successfully!"
                                    description={
                                        <Space direction="vertical" size="small" style={{ width: '100%' }}>
                                            <span>Great! Your lot images have been uploaded. Would you like to create more lots for this auction?</span>
                                            <Button
                                                type="primary"
                                                icon={<PlusOutlined />}
                                                onClick={() => navigate(`/lots/create?auction_slug=${auctionSlug}`)}
                                            >
                                                Create More Lots Now
                                            </Button>
                                        </Space>
                                    }
                                    type="success"
                                    showIcon
                                    closable
                                    onClose={() => setShowSuccessAlert(false)}
                                    style={{ marginBottom: 12 }}
                                />
                            )}

                            {isAuctionCompleted && (
                                <Alert
                                    message="Auction Completed"
                                    description="This auction is already completed. You cannot upload images to lots in completed auctions."
                                    type="warning"
                                    showIcon
                                    style={{ marginBottom: 12 }}
                                />
                            )}

                            <ImageUpload
                                entityType="lot"
                                entityId={lot?.id}
                                entitySlug={lotSlug}
                                existingImages={lot?.images || []}
                                onSuccess={handleImageUploadSuccess}
                                showDragger={true}
                                disabled={isAuctionCompleted}
                            />
                        </Space>
                    </div>
                </div>

                <Modal
                    title={
                        <Space>
                            <CheckCircleOutlined style={{ color: '#52c41a', fontSize: 'clamp(20px, 4vw, 24px)' }} />
                            <span style={{ fontSize: 'clamp(16px, 3vw, 18px)' }}>Images Uploaded Successfully!</span>
                        </Space>
                    }
                    open={showSuccessModal}
                    onCancel={() => setShowSuccessModal(false)}
                    footer={[
                        <Checkbox 
                            key="dontShow" 
                            checked={dontShowAgain}
                            onChange={handleDontShowAgain}
                            style={{ 
                                float: isSmallMobile ? 'none' : 'left',
                                display: isSmallMobile ? 'block' : 'inline-block',
                                width: isSmallMobile ? '100%' : 'auto',
                                marginBottom: isSmallMobile ? '8px' : '0'
                            }}
                        >
                            Don't show this again
                        </Checkbox>,
                        <Button 
                            key="back" 
                            onClick={() => setShowSuccessModal(false)}
                            block={isSmallMobile}
                            style={{ 
                                marginBottom: isSmallMobile ? '8px' : '0'
                            }}
                        >
                            Continue Managing Images
                        </Button>,
                        <Button 
                            key="submit" 
                            type="primary" 
                            icon={<RocketOutlined />}
                            onClick={handleCreateMoreLots}
                            block={isSmallMobile}
                        >
                            Create More Lots
                        </Button>
                    ]}
                    width={isSmallMobile ? '95%' : 600}
                    style={{ top: isSmallMobile ? 20 : undefined }}
                >
                    <Space direction="vertical" size="large" style={{ width: '100%' }}>
                        <Text style={{ fontSize: 'clamp(14px, 2.5vw, 16px)' }}>
                            Great job! Your lot images have been uploaded successfully. Would you like to create more lots for this auction?
                        </Text>

                        <Steps
                            direction={isSmallMobile ? "horizontal" : "vertical"}
                            current={0}
                            items={[
                                {
                                    title: 'Create More Lots',
                                    description: isSmallMobile ? '' : 'Add more items to your auction with detailed descriptions and specifications.',
                                    icon: <PlusOutlined />
                                },
                                {
                                    title: 'Add Lot Images',
                                    description: isSmallMobile ? '' : 'Upload high-quality images for each new lot to showcase your items effectively.',
                                    icon: <PictureOutlined />
                                }
                            ]}
                            style={{ 
                                width: '100%',
                                overflowX: isSmallMobile ? 'auto' : 'visible'
                            }}
                        />

                        <Alert
                            message="Pro Tip"
                            description="Adding multiple high-quality images from different angles can significantly increase bidder interest and final sale prices."
                            type="info"
                            showIcon
                            style={{ fontSize: 'clamp(12px, 2vw, 14px)' }}
                        />
                    </Space>
                </Modal>
            </Spin>
        </DashboardLayout>
    );
};

export default LotImages; 